import {FILTER_UNASSIGNED_ID, FilterGroupState, FilterKey, FilterStore, FilterItemDefinition, SeasonalFilterItemDefinition, DEBUG_FILTERS} from '@modules/catalog'
import {onSiteError} from '@stores'
import {action, computed, observable, runInAction, toJS, autorun, when} from 'mobx'
import { utils, _ } from '@api'

export class FilterGroupStore<DataShape extends { [key: string]: Array<FilterItemDefinition> }, ItemState = FilterItemDefinition> {
  constructor(public filter: FilterStore, public groupName: FilterKey, private loadFromES: () => Promise<{data: Partial<DataShape>, count: number}>, onConstruct = (store: FilterGroupStore<DataShape, ItemState>) => {}) {
    let {groups, group} = this

    if (!group) {
      DEBUG_FILTERS && console.debug(`Creating missing group state for ${groupName}`)
      group = {
        key: groupName,
        selected: new Map<string, any>(),
        excluded: new Map<string, any>(),
        store: this,
        groupedByValue: {}
      }

      // YFTS-9738 Filter string serialized values are lost due to race here

      groups[groupName] = group
    }
    else {
      group.store = this
    }

    onConstruct(this)
  }

  get catalog() { return this.filter.catalog}

  @computed get catalogField() {
    const {groupName} = this
    return this.catalog._fieldByName[_.last(groupName.split('!'))]  // In library workspaces we show the same filter fields in the header AND in the filter sidebar and they will conflict on name
  }

  @observable initialLoad = true
  @observable loading = false
  @observable.ref data: Partial<DataShape> = {}
  /**
   * Total distinct item count, different than known distinct items
   */
  @observable count = null

  get groups() { return this.filter.state.groups}

  @computed get group() { return this.groups[this.groupName] as FilterGroupState<ItemState>}

  @action clearFilter = () => {
    this.group.selected.clear()
  }

  @action refetch = async () => {
    if (this.loading) {
      when(() => !this.loading, () => this.refetch())
    }

    this.loading = true

    const {group: {selected}, groupName} = this

    try {
      selected.delete(null)
      await utils.sleep(0) // To allow all the computeds to settle on initial filter group creation

      const r = await this.loadFromES()
      r && runInAction(`Save refetched data for ${groupName}`, () => {
        Object.assign(this, r)

        // console.log({groupName, r})
        if (selected.size) {
          const remainingToSet = new Set<string>(Array.from(selected.keys()).filter(k => !selected.get(k))),
            entries = r.data[_.keys(r.data)[0]]

          if (_.isArray(entries)) {
            for (var item of entries) {
              const id = (item.compoundId ?? item.id)?.toString()
              if (remainingToSet.has(id)) {
                remainingToSet.delete(id)
                selected.set(id, null)

                if (remainingToSet.size == 0) {
                  break
                }
              }
            }
          }
          else {
            console.warn(`Unable to reset selected filter values for ${groupName} - this will break filter chips for this group`)
          }
        }
      })

      this.loading = false
      this.initialLoad = false
    }
    catch (err) {
      onSiteError(err)
      this.loading = false
      this.initialLoad = false
    }
  }
}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/components/internal/FilterGroupStore.ts